import React, { useEffect, useMemo, useState } from 'react';
import { academyAdminApi } from '../../services/api';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import { formatDate } from '../../utils/formatDate';

type AcademyRow = {
  id: number;
  courseId: number;
  courseTitle: string;
  userId: number;
  email?: string;
  status: string;
  enrollmentDate: string;
};

const AcademyReportsAdminPage: React.FC = () => {
  const [rows, setRows] = useState<AcademyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [programFilter, setProgramFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const listRes = await fetch(`${apiUrl(API_PATHS.courses.list)}?enrollmentAllowedOnly=false`, {
          headers: apiHeaders(localStorage.getItem('token')),
        });
        const listBody = await listRes.json();
        const programs = (listBody?.courses || []) as Array<{ id: number; title?: string }>;
        const collected: AcademyRow[] = [];
        for (const program of programs) {
          const response = await academyAdminApi.enrollmentsByCourse(program.id);
          const enrollments = response.data.courseEnrollments
            || (response.data as { CourseEnrollments?: typeof response.data.courseEnrollments }).CourseEnrollments
            || [];
          for (const row of enrollments) {
            collected.push({
              id: row.id,
              courseId: row.courseId || program.id,
              courseTitle: row.courseTitle || program.title || `Program ${program.id}`,
              userId: row.userId,
              status: row.status,
              enrollmentDate: row.enrollmentDate,
            });
          }
        }
        const ids = [...new Set(collected.map((row) => row.userId).filter((id) => id > 0))];
        if (ids.length) {
          const profilesResponse = await fetch(apiUrl(API_PATHS.userProfile.byIds), {
            method: 'POST',
            headers: { ...apiHeaders(localStorage.getItem('token')), 'Content-Type': 'application/json' },
            body: JSON.stringify({ userProfileIds: ids }),
          }).then((res) => res.json()).catch(() => null);
          const emailById: Record<number, string> = {};
          for (const profile of (profilesResponse?.userProfiles || []) as Array<{ id?: number; email?: string }>) {
            const id = Number(profile.id);
            if (id > 0 && profile.email) emailById[id] = profile.email;
          }
          collected.forEach((row) => {
            row.email = emailById[row.userId];
          });
        }
        if (!cancelled) setRows(collected);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load academy enrollments.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => [...new Set(rows.map((row) => row.courseTitle))].sort(), [rows]);
  const statuses = useMemo(() => [...new Set(rows.map((row) => row.status).filter(Boolean))].sort(), [rows]);
  const filtered = rows.filter((row) => {
    if (programFilter && row.courseTitle !== programFilter) return false;
    if (statusFilter && row.status !== statusFilter) return false;
    return true;
  });

  if (loading) return <p className="text-gray-600">Loading academy enrollments…</p>;
  if (error) return <p className="text-error-700">{error}</p>;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">IDC Academy program enrollments only. Course catalog enrollments are on Course reports.</p>
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="text-sm text-gray-700">
          Program
          <select className="ml-2 rounded-md border-gray-300" value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
            <option value="">All</option>
            {programs.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Status
          <select className="ml-2 rounded-md border-gray-300" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {statuses.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2">Program</th>
              <th className="px-3 py-2">Learner</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Enrolled</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-t border-gray-100">
                <td className="px-3 py-2">{row.courseTitle}</td>
                <td className="px-3 py-2">{row.email || `Profile #${row.userId}`}</td>
                <td className="px-3 py-2">{row.status}</td>
                <td className="px-3 py-2">{formatDate(row.enrollmentDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-4 text-gray-500">No academy enrollments found.</p>}
      </div>
    </div>
  );
};

export default AcademyReportsAdminPage;
